import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';

import { CompanyExternalIds, CompanyModel, UpdateCbpAssignedNumberRegistrationDetailsModel } from '@data-layer/models/companies';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertService } from '@services/alert.service';
import { enumRespErrors } from '@data-layer/enums/common';

@Injectable({
  providedIn: 'root',
})
export class CompaniesRepository {
  constructor(private apiService: ApiService, public alertService: AlertService) {}

  public getCompanies(isShowLoader: boolean = true): Observable<CompanyModel[]> {
    return this.apiService.get<CompanyModel[]>(environment.url.companies, null, { isShowLoader });
  }

  public getCompanyById(companyId: number, isShowLoader: boolean = true): Observable<CompanyModel> {
    return this.apiService.get<CompanyModel>(`${environment.url.companies}${companyId}`, null, { isShowLoader });
  }

  public updateCompanyValidationState(id: number, validationArea: enumCompanyPlacement): Observable<CompanyModel> {
    return this.apiService.put<CompanyModel>(`${environment.url.companies}${id}/validation-area`, validationArea);
  }

  public create(company: CompanyModel, token?: string): Observable<CompanyModel> {
    const options: any = !!token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : null;
    return this.apiService.post<CompanyModel>(environment.url.companies, company, options);
  }

  public update(id: number, company: CompanyModel): Observable<CompanyModel> {
    return this.apiService.put<CompanyModel>(`${environment.url.companies}${id}`, company);
  }

  public getBrokerPoaFile(companyId: number, isShowLoader: boolean = false): Observable<{ poaTemplateUrl: string }> {
    return this.apiService.get<{ poaTemplateUrl: string }>(`${environment.url.companies}${companyId}/broker-poa-file`, null, {
      isShowLoader,
    });
  }

  public sendNotify(id: number, notificationType: string, settingValue: boolean): Observable<CompanyModel> {
    return this.apiService.put(`${environment.url.companies}${id}/notifications`, { notificationType, settingValue });
  }

  public acceptAuthorizationAgreement(id: number, value: boolean): Observable<any> {
    return this.apiService.post(`${environment.url.companies}${id}/accept-authorization-agreement`, value);
  }

  public acknowledgeMigrationPopup(companyId: number): Observable<void> {
    return this.apiService.post<void>(`${environment.url.companies}${companyId}/migration-popup/acknowledge`, null);
  }

  public isCouponsAvailable(companyId: number, productCodes: string[]): Observable<boolean> {
    let query: string = '';
    (productCodes || []).forEach((code: string, index: number) => {
      query += productCodes.length - 1 === index ? `ProductCodes=${code}` : `ProductCodes=${code}&`;
    });
    return productCodes?.length > 0
      ? this.apiService.get<boolean>(`${environment.url.prefixCustoms}v2/companies/${companyId}/has-available-discounts?${query}`)
      : of(false);
  }

  public getCompanyExternalIds(companyId: number): Observable<CompanyExternalIds> {
    return this.apiService.get<CompanyExternalIds>(`${environment.url.companies}${companyId}/external-ids`);
  }

  public deleteDefaultPaymentMethod(id: number, isShowLoader: boolean): Observable<CompanyModel> {
    return this.apiService.delete<CompanyModel>(`${environment.url.companies}${id}/default-payment-method`, null, { isShowLoader });
  }

  public handleInvitationError(err: HttpErrorResponse): Observable<any> {
    const errors: { [name: string]: string } = {
      invitation_overdue: 'BACK_ERROR.INVITATION_OVERDUE',
      invitation_already_accepted: 'BACK_ERROR.INVITATION_ALREADY_ACCEPTED',
      invite_already_activated: 'BACK_ERROR.INVITATION_ALREADY_ACTIVATED',
      invite_already_blocked: 'BACK_ERROR.INVITATION_ALREADY_BLOCKED',
      entity_is_not_found: 'BACK_ERROR.INVITATION_DEFAULT',
    };

    switch (err.error?.tag) {
      case enumRespErrors.InvitationOverdue:
      case enumRespErrors.InvitationAlreadyAccepted:
      case enumRespErrors.InviteAlreadyBlocked:
        this.alertService.modalError(errors[err.error?.tag]);
        break;
      case enumRespErrors.EntityIsNotFound:
        this.alertService.error(errors.entity_is_not_found);
        break;
      case enumRespErrors.InviteAlreadyActivated:
        break;
      default:
        this.alertService.error(errors.entity_is_not_found);
    }

    return of();
  }

  public deleteCompany(companyId: number): Observable<void> {
    return this.apiService.delete<void>(`${environment.url.companies}${companyId}`);
  }

  public updateCbpAssignedNumberRegistrationDetails(
    companyId: number,
    details: UpdateCbpAssignedNumberRegistrationDetailsModel,
  ): Observable<void> {
    return this.apiService.put<void>(
      `${environment.url.prefixCustoms}v2/companies/${companyId}/cbp-assigned-number-registration-details`,
      details,
    );
  }
}
